var trackError = require('trackError')
var assign = require('lodash').assign
var CustomEvent = require('../constants/customEvent')

var SEGMENT_WRITE_KEY = window.ENV ? window.ENV.SEGMENT_WRITE_KEY : process.env.SEGMENT_WRITE_KEY

var THEME_RESOLVED_TRACKING_EVENT = 'xxx - Bookkeeping - Theme resolved'
var THEME_RESOLVED_STORAGE_KEY = 'lastTrackedThemeResolved'

module.exports = Em.Object.extend({
    userOrganizations: Em.inject.service(),

    sideMenu: Em.inject.service(),

    user: function() {
        return this.container.lookup('controller:user').get('model')
    }.property(),

    getIdentificationId: function(organization) {
        return organization.get('globalId')
    },

    getAdvisorTrackingProperties: function(organization) {
        var activeMembership = this.get('userOrganizations').get('activeMembership')
        var activeUmbrella = this.get('sideMenu').get('activeUmbrella')
        var isPracticeMode = !!activeUmbrella
        var isViaUmbrella = activeMembership && activeMembership.via === 'umbrella'
        var isCpa = isPracticeMode || isViaUmbrella

        var actorType = isCpa ? 'cpa' : 'customer'

        var advisorLayerMode = null
        try {
            advisorLayerMode = localStorage.getItem('advisorLayerMode')
        } catch(e) {}

        var isExpertMode = isCpa && advisorLayerMode === 'expert'

        var appArea
        if (isPracticeMode) {
            appArea = 'practice'
        } else if (isExpertMode) {
            appArea = 'expert'
        } else {
            appArea = 'client'
        }

        var cpaId = isCpa && organization ? organization.get('ownerUmbrellaId') : undefined

        return {
            actor_type: actorType,
            app_area: appArea,
            cpa_id: cpaId
        }
    },

    /**
     * Tracks a segment event
     */
    track: function(event, payload) {
        if (!window.analytics) {
            trackError('Analytics script not present')
            return
        }

        var organization = this.container.lookup('controller:organization').get('model')
        var user = this.container.lookup('controller:user').get('model')
        var userId = user && user.get('id')
        var userEmail = user && user.get('email')
        var advisorProperties = this.getAdvisorTrackingProperties(organization)

        if (!organization) {
            window.analytics.track(event, assign({}, advisorProperties, payload))
            return
        }

        var identificationId = this.getIdentificationId(organization)

        var deviceData = {
            screen_width: window.screen.width,
            screen_height: window.screen.height
        }

        var organizationData = {
            id: identificationId,
            organization_id: identificationId,
            name: organization.get('name'),
            email: organization.get('ownerUmbrellaId') ? '' : organization.get('email'),
            billing_plan: organization.get('subscriptionProductPlan'),
            billing_period: organization.get('subscriptionPeriod'),
            is_trial: organization.get('isTrial'),
            brand: organization.get('brand'),
            current_user_id: user.get('globalId'),
            client: 'webapp',
            ui_setup: organization.get('uiSetup'),
            country: organization.get('country.id')
        }

        if (!identificationId || !userId) {
            try {
                var noOrganizationDataTraits = {
                    event: event,
                    organization_url: organization.get('url'),
                    debug_organization_id: organization.get('id'),
                    path: window.location.pathname,
                    source: organization.get('source'),
                    client: 'webapp'
                }

                window.analytics.track('XXX - Debug - Possible no organization data', assign(organizationData, noOrganizationDataTraits, deviceData))
            } catch(error) {
                var noOrganizationDataFailedTraits = {
                    error: error.message,
                    event: event,
                    organization_url: organization.get('url'),
                    path: window.location.pathname,
                    client: 'webapp'
                }

                window.analytics.track('XXX - Debug - Possible no organization data - failed to get id', assign(organizationData, noOrganizationDataFailedTraits, deviceData))
            }
            return
        }

        var facebookRequiredEvents = {
            action_source: 'website',
            client_user_agent: navigator.userAgent,
            event_source_url: window.location.href
        }

        var userData = !userId ? {} : {
            user_id: organization.get('id'),
            email: userEmail
        }

        window.analytics.track(event, assign(organizationData, userData, facebookRequiredEvents, deviceData, advisorProperties, payload))
    },

    /**
     * Returns an object containing identification traits for the given organization.
     *
     * @param {string} identificationId - The identification ID of the organization.
     * @param {Object} organization - The organization object.
     * @returns {Object} An object containing identification traits.
     */
    getIdentificationTraits: function(identificationId, organization) {
        return {
            id: identificationId,
            organization_id: identificationId,
            name: organization.get('name'),
            created_at: organization.get('createdTime').format(),
            email: organization.get('ownerUmbrellaId') ? '' : organization.get('email'),
            current_billing_period: organization.get('subscriptionPeriod'),
            current_billing_plan: organization.get('subscriptionProductPlan'),
            vat_number: organization.get('registrationNo'),
            current_brand: organization.get('brand'),
            country: organization.get('country.id'),
            is_test: organization.get('isBrandAgeras') ? organization.get('isTest') : undefined, // in Billy case this is handled by BE event
            random_bucket: organization.get('randomBucket'),
            currency: organization.get('baseCurrencyId'),
            vat_period: organization.get('salesTaxPeriod'),
            coa_type: organization.get('chartOfAccountId'),
            url: organization.get('url'),
            owner_user_id: organization.get('ownerUserId'),
            owner_umbrella_id: organization.get('ownerUmbrellaId'),
            trial_expiry_date: organization.get('trialExpiryDate'),
            consolidated_billing: organization.get('consolidatedBilling'),
            locale_id: organization.get('localeId'),
            billing_platform: organization.get('billingType') === 'external' ? 'upodi' : 'billy',
            is_trial: organization.get('isTrial'),
            is_beta_candidate: organization.get('settings.isBetaCandidate') === '1',
            ui_setup: organization.get('uiSetup')
        }
    },

    /**
     * Identifies the given organization to the analytics service.
     *
     * @param {Object} organization - The organization object.
     */
    identify: function(organization) {
        var self = this
        if (!window.analytics) {
            trackError('Analytics script not present')
            return
        }

        if (!organization) {
            trackError('Organization not present')
            return
        }

        var identificationId = this.getIdentificationId(organization)

        window.analytics.ready(function() {
            window.analytics.identify(identificationId, self.getIdentificationTraits(identificationId, organization))
        })
    },

    /**
     * Forces identification of the given organization to the analytics service without checking analytics service presence.
     *
     * @param {Object} organization - The organization object.
     */
    forceIdentify: function(organization) {
        var self = this
        if (!window.analytics) {
            trackError('Analytics script not present')
            return
        }

        if (!organization) {
            trackError('Organization not present')
            return
        }

        var identificationId = this.getIdentificationId(organization)

        window.analytics.identify(identificationId, self.getIdentificationTraits(identificationId, organization))
    },

    setAnonymousId: function(anonymousId) {
        if (!window.analytics) {
            return
        }

        try {
            if (window.analytics.setAnonymousId) {
                window.analytics.setAnonymousId(anonymousId)
            } else if (SEGMENT_WRITE_KEY) {
                window.analytics.load(SEGMENT_WRITE_KEY)
                window.analytics.page()
                window.analytics.setAnonymousId(anonymousId)
            }
        } catch(error) {
            trackError(error)
        }
    },

    /* Track what theme customers actually use. We can already see light/dark explicitly in the DB, but auto is ambiguous. This will send events on session start with resolved theme based
    on system preference, and will send an event when the system theme changes
    (like light to dark in the evening etc) */
    listenThemeResolved: function() {
        var self = this

        window.addEventListener(CustomEvent.ThemeLayoutChanged, function(event) {
            var themeMode = window.Billy && window.Billy.themeLayoutMode

            if (themeMode !== 'auto') {
                return
            }

            var themeResolved = event.detail
            var previouslyTracked

            try {
                previouslyTracked = window.sessionStorage.getItem(THEME_RESOLVED_STORAGE_KEY)
            } catch(e) {}

            if (previouslyTracked === themeResolved) {
                return
            }

            self.track(THEME_RESOLVED_TRACKING_EVENT, {
                themeResolved: themeResolved,
                trigger: previouslyTracked ? 'system_change' : 'session_start'
            })

            try {
                window.sessionStorage.setItem(THEME_RESOLVED_STORAGE_KEY, themeResolved)
            } catch(e) {}
        })
    }.on('init')
})
